import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import EventTimeline from '../../components/EventTimeline';
import { formatDietaryRestrictions } from '../../utils/dietaryUtils';
import './EventDetails.css';

// Fix for default marker icons in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Create numbered markers
const createNumberedIcon = (number: number, color: string = '#4287f5') => {
  return L.divIcon({
    className: 'custom-numbered-icon',
    html: `<div style="
      background-color: ${color};
      color: white;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: 18px;
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    ">${number}</div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20]
  });
};

// Component to auto-fit map bounds
function MapBounds({ coordinates }: { coordinates: [number, number][] }) {
  const map = useMap();
  const [hasSetBounds, setHasSetBounds] = React.useState(false);

  useEffect(() => {
    if (coordinates.length > 0 && !hasSetBounds) {
      const bounds = L.latLngBounds(coordinates);
      map.fitBounds(bounds, { padding: [50, 50] });
      setHasSetBounds(true);
    }
  }, [coordinates.length, map, hasSetBounds]);

  return null;
}

interface Event {
  id: string;
  name: string;
  description: string;
  city: string;
  area: string;
  scheduledDate: string;
  registrationDeadline: string;
  status: string;
  organizerName?: string;
  organizerEmail?: string;
  organizerPhone?: string;
  finalGatheringLocation: {
    name: string;
    address: string;
    lat?: number;
    lon?: number;
  };
  routeGenerated: boolean;
  schedule?: {
    appetizerDuration: number;
    mainCourseDuration: number;
    dessertDuration: number;
  };
  totalParticipants?: number;
}

interface Participant {
  userId: string | null;
  name: string;
  email: string;
  phone: string;
  dietaryRestrictions: string | null;
}

interface Pair {
  id: string;
  participant1: Participant;
  participant2: Participant;
  address: string;
  lat?: number;
  lon?: number;
  hostingMeal: 'appetizer' | 'mainCourse' | 'dessert' | null;
  registeredAt: string;
  notes: string | null;
}

interface RouteStop {
  course: 'appetizer' | 'mainCourse' | 'dessert' | 'party';
  hostPairId?: string;
  hostAddress: string;
  hostLat?: number;
  hostLon?: number;
  hostNames?: string;
  hostMessage?: string;
  distance: number;
  hosts: Participant[];
  guests: Participant[];
}

interface Route {
  stops: RouteStop[];
  totalDistance: number;
}

const EventDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { accessToken, user } = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [pair, setPair] = useState<Pair | null>(null);
  const [route, setRoute] = useState<Route | null>(null);
  const [timeline, setTimeline] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUnsubscribeConfirm, setShowUnsubscribeConfirm] = useState(false);
  const [unsubscribing, setUnsubscribing] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'registration' | 'routes' | 'timeline' | 'logs' | 'settings'>('overview');
  const [logs, setLogs] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // Handle hash navigation to tabs
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash === 'din-rute') {
      setActiveTab('routes');
    }
  }, []);

  useEffect(() => {
    if (id && accessToken) {
      fetchEventDetails();
      fetchEventTimeline();
    }
  }, [id, accessToken]);

  useEffect(() => {
    if (activeTab === 'logs' && id && accessToken && user) {
      fetchLogs();
    }
  }, [activeTab, id, accessToken, user]);

  const fetchLogs = async () => {
    if (!id || !user?.id) return;
    
    setLogsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/events/${id}/logs`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        timeout: 10000
      });
      
      // Logs are already filtered by the backend to only include this user's emails
      setLogs(response.data.logs || []);
    } catch (err: any) {
      console.error('Failed to fetch logs:', err);
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const fetchEventDetails = async () => {
    try {
      const response = await axios.get(`${API_URL}/events/${id}/details`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      setEvent(response.data.event);
      setPair(response.data.pair);
      setRoute(response.data.route || null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke laste arrangementsdetaljer');
      console.error('Error fetching event details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEventTimeline = async () => {
    try {
      const response = await axios.get(`${API_URL}/organizers/events/${id}/timeline`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        timeout: 10000
      });

      setTimeline(response.data.formattedTimeline);
    } catch (err) {
      console.error('Error fetching event timeline:', err);
    }
  };

  const canUnsubscribe = () => {
    if (!event) {
      return false;
    }
    if (event.status !== 'registration') {
      return false;
    }
    
    const deadline = new Date(event.registrationDeadline);
    const now = new Date();
    const oneDayInMs = 24 * 60 * 60 * 1000;
    const timeUntilDeadline = deadline.getTime() - now.getTime();
    
    return timeUntilDeadline > oneDayInMs;
  };

  const handleUnsubscribe = async () => {
    if (!id) return;
    
    setUnsubscribing(true);
    setError('');
    
    try {
      await axios.delete(`${API_URL}/events/${id}/unsubscribe`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      
      // Redirect to event list with success message
      window.location.href = '/deltaker/mine-fester?unsubscribed=true';
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke melde av');
      setShowUnsubscribeConfirm(false);
    } finally {
      setUnsubscribing(false);
    }
  };

  const downloadDiploma = async () => {
    if (!id || !accessToken) return;

    try {
      const response = await axios.get(
        `${API_URL}/diploma/${id}/diploma`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sykkelfest-diplom-${event?.name.replace(/\s+/g, '-')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading diploma:', err);
      alert('Kunne ikke laste ned diplom. Prøv igjen senere.');
    }
  };

  const getMealIcon = (meal: string) => {
    switch (meal) {
      case 'appetizer':
        return '🥗';
      case 'mainCourse':
        return '🍝';
      case 'dessert':
        return '🍰';
      case 'party':
        return '🎉';
      default:
        return '🍽️';
    }
  };

  const getMealName = (meal: string) => {
    switch (meal) {
      case 'appetizer':
        return 'Forrett';
      case 'mainCourse':
        return 'Hovedrett';
      case 'dessert':
        return 'Dessert';
      case 'party':
        return 'Etterfest';
      default:
        return meal;
    }
  };

  // Get course time from timeline
  const getCourseTime = (course: string): Date | null => {
    if (!timeline || !event) return null;
    
    const eventDate = new Date(event.scheduledDate);
    let timeString = '';
    
    switch (course) {
      case 'appetizer':
        timeString = timeline.starterTime;
        break;
      case 'mainCourse':
        timeString = timeline.mainCourseTime;
        break;
      case 'dessert':
        timeString = timeline.dessertTime;
        break;
      default:
        return null;
    }
    
    if (!timeString) return null;
    
    const [hours, minutes] = timeString.split(':').map(Number);
    const courseDateTime = new Date(eventDate);
    courseDateTime.setHours(hours, minutes, 0, 0);
    
    return courseDateTime;
  };

  // Check if address should be revealed based on timeline
  const shouldRevealAddress = (course: string): boolean => {
    if (isCompleted) return true;
    
    const now = new Date();
    
    // Different reveal times based on course
    switch (course) {
      case 'appetizer':
        // Forrett: revealed 30 minutes before it starts
        const appetizerTime = getCourseTime('appetizer');
        if (!appetizerTime) return false;
        const appetizerRevealTime = new Date(appetizerTime.getTime() - 30 * 60 * 1000);
        return now >= appetizerRevealTime;
        
      case 'mainCourse':
        // Hovedrett: revealed at mid-time of Forrett
        const appetizerStart = getCourseTime('appetizer');
        if (!appetizerStart || !event?.schedule?.appetizerDuration) return false;
        const mainCourseRevealTime = new Date(appetizerStart.getTime() + (event.schedule.appetizerDuration / 2) * 60 * 1000);
        return now >= mainCourseRevealTime;
        
      case 'dessert':
        // Dessert: revealed at mid-time of Hovedrett
        const mainCourseStart = getCourseTime('mainCourse');
        if (!mainCourseStart || !event?.schedule?.mainCourseDuration) return false;
        const dessertRevealTime = new Date(mainCourseStart.getTime() + (event.schedule.mainCourseDuration / 2) * 60 * 1000);
        return now >= dessertRevealTime;
        
      case 'party':
        // Party address is always revealed
        return true;
        
      default:
        return false;
    }
  };

  // Check if guest list should be revealed based on timeline
  const shouldRevealGuests = (course: string): boolean => {
    if (isCompleted) return true;
    
    const courseTime = getCourseTime(course);
    if (!courseTime) return false;
    
    const now = new Date();
    return now >= courseTime;
  };

  // Get party time (dessert time + dessert duration)
  const getPartyTime = (): Date | null => {
    const dessertTime = getCourseTime('dessert');
    if (!dessertTime || !event?.schedule?.dessertDuration) return null;
    
    const partyTime = new Date(dessertTime);
    partyTime.setMinutes(partyTime.getMinutes() + event.schedule.dessertDuration);
    return partyTime;
  };

  // Get home start time (30 minutes before appetizer - when address is revealed)
  const getHomeStartTime = (): Date | null => {
    const appetizerTime = getCourseTime('appetizer');
    if (!appetizerTime) return null;
    
    const homeStartTime = new Date(appetizerTime);
    homeStartTime.setMinutes(homeStartTime.getMinutes() - 30);
    return homeStartTime;
  };

  // Create clickable map link for address
  const getMapLink = (address: string): string => {
    const encodedAddress = encodeURIComponent(address);
    // Universal map link that works on both iOS and Android
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  };

  if (loading) {
    return (
      <div className="event-details-page">
        <div className="loading">Laster arrangementsdetaljer...</div>
      </div>
    );
  }

  if (error || !event || !pair) {
    return (
      <div className="event-details-page">
        <div className="error-message">{error || 'Arrangement ikke funnet'}</div>
        <Link to="/deltaker/mine-fester" className="btn btn-secondary">
          Tilbake til mine fester
        </Link>
      </div>
    );
  }

  const eventDate = new Date(event.scheduledDate);
  const isUpcoming = eventDate > new Date();
  const isCompleted = event.status === 'completed';
  const hasRoute = event.routeGenerated && route;

  // Determine which meal the user is hosting from route data
  const getHostingMeal = (): 'appetizer' | 'mainCourse' | 'dessert' | null => {
    if (!route || !pair) return null;
    const hostingStop = route.stops.find(stop => stop.course !== 'party' && stop.hostPairId === pair.id);
    return hostingStop ? hostingStop.course as 'appetizer' | 'mainCourse' | 'dessert' : null;
  };

  const hostingMeal = getHostingMeal();

  return (
    <div className="event-details-page">
      <div className="event-details-container">
        <div className="page-header">
          <h1>{event.name}</h1>
          <Link to="/deltaker/mine-fester" className="btn btn-secondary">
            ← Tilbake
          </Link>
        </div>

        {/* Tabbed Section */}
        <div className="tabbed-section">
          <div className="tabs">
            <button 
              className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              📊 Arrangement
            </button>
            <button 
              className={`tab ${activeTab === 'registration' ? 'active' : ''}`}
              onClick={() => setActiveTab('registration')}
            >
              👥 Din Påmelding
            </button>
            <button 
              className={`tab ${activeTab === 'routes' ? 'active' : ''}`}
              onClick={() => setActiveTab('routes')}
            >
              🚴 Din Rute {hasRoute ? '✅' : ''}
            </button>
            <button 
              className={`tab ${activeTab === 'timeline' ? 'active' : ''}`}
              onClick={() => setActiveTab('timeline')}
            >
              📅 Tidsplan
            </button>
            <button 
              className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
              onClick={() => setActiveTab('logs')}
            >
              📋 Logg
            </button>
            <button 
              className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              ⚙️ Innstillinger
            </button>
          </div>

          <div className="tab-content">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="details-card">
                <h2>📅 Arrangementsoversikt</h2>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Dato og tid</span>
                    <span className="info-value">
                      {eventDate.toLocaleDateString('nb-NO', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                      <br />
                      {eventDate.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Område</span>
                    <span className="info-value">{event.city}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Sluttdestinasjon</span>
                    <span className="info-value">
                      {event.finalGatheringLocation.name}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Status</span>
                    <span className={`status-badge status-${event.status}`}>
                      {event.status === 'registration' && '📝 Påmelding'}
                      {event.status === 'confirmed' && '✅ Bekreftet'}
                      {event.status === 'active' && '🚴 Pågår'}
                      {event.status === 'completed' && '🏁 Fullført'}
                      {event.status === 'cancelled' && '🚫 Avlyst'}
                    </span>
                  </div>
                </div>
                {event.description && (
                  <div className="description">
                    <p>{event.description}</p>
                  </div>
                )}

                {/* Feedback Button - Only show for completed events */}
                {isCompleted && (
                  <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#EFF6FF', borderRadius: '8px', borderLeft: '4px solid #2563EB', textAlign: 'center' }}>
                    <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', fontWeight: 'bold', color: '#1E40AF' }}>
                      🌟 Del din opplevelse
                    </h3>
                    <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#1E40AF' }}>
                      Hjelp oss å forbedre Sykkelfest ved å dele din tilbakemelding.
                    </p>
                    <Link 
                      to={`/feedback/${id}`}
                      className="btn btn-primary"
                      style={{ padding: '0.75rem 1.5rem', fontSize: '1rem', textDecoration: 'none', display: 'inline-block' }}
                    >
                      💬 Gi tilbakemelding
                    </Link>
                  </div>
                )}

                {/* Download Diploma Button - Only show for completed events */}
                {isCompleted && (
                  <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#F0FDF4', borderRadius: '8px', borderLeft: '4px solid #10B981', textAlign: 'center' }}>
                    <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', fontWeight: 'bold', color: '#065F46' }}>
                      🎓 Gratulerer med fullført Sykkelfest!
                    </h3>
                    <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#047857' }}>
                      Last ned ditt personlige diplom som minne fra arrangementet.
                    </p>
                    <button 
                      onClick={downloadDiploma}
                      className="btn btn-primary"
                      style={{ padding: '0.75rem 1.5rem', fontSize: '1rem' }}
                    >
                      📥 Last ned diplom
                    </button>
                  </div>
                )}

                {/* Organizer Contact Information */}
                {(event.organizerName || event.organizerEmail || event.organizerPhone) && (
                  <div className="organizer-contact" style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#F0F9FF', borderRadius: '8px', borderLeft: '4px solid #2563EB' }}>
                    <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', fontWeight: 'bold', color: '#2563EB' }}>
                      📞 Kontakt arrangør
                    </h3>
                    {event.organizerName && (
                      <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                        <strong>Navn:</strong> {event.organizerName}
                      </p>
                    )}
                    {event.organizerEmail && (
                      <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                        <strong>E-post:</strong> <a href={`mailto:${event.organizerEmail}`} style={{ color: '#2563EB', textDecoration: 'none' }}>{event.organizerEmail}</a>
                      </p>
                    )}
                    {event.organizerPhone && (
                      <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                        <strong>Telefon:</strong> <a href={`tel:${event.organizerPhone}`} style={{ color: '#2563EB', textDecoration: 'none' }}>{event.organizerPhone}</a>
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Registration Tab */}
            {activeTab === 'registration' && (
              <div className="details-card">
                <h2>👥 Din påmelding</h2>
                <div className="pair-info">
                  <div className="participant-card">
                    <h3>Deltaker 1</h3>
                    <p><strong>{pair.participant1.name}</strong></p>
                    <p>📧 {pair.participant1.email}</p>
                    <p>📱 {pair.participant1.phone}</p>
                    {formatDietaryRestrictions(pair.participant1.dietaryRestrictions) && (
                      <p className="dietary-info">⚠️ Diett/allergier: {formatDietaryRestrictions(pair.participant1.dietaryRestrictions)}</p>
                    )}
                  </div>
                  <div className="participant-card">
                    <h3>Deltaker 2</h3>
                    <p><strong>{pair.participant2.name}</strong></p>
                    <p>📧 {pair.participant2.email}</p>
                    <p>📱 {pair.participant2.phone}</p>
                    {formatDietaryRestrictions(pair.participant2.dietaryRestrictions) && (
                      <p className="dietary-info">⚠️ Diett/allergier: {formatDietaryRestrictions(pair.participant2.dietaryRestrictions)}</p>
                    )}
                  </div>
                </div>
                
                <div className="hosting-info">
                  <h3>Vertsadresse</h3>
                  <p>📍 {pair.address}</p>
                  {hostingMeal && (
                    <p>{getMealIcon(hostingMeal)} {getCourseTime(hostingMeal)?.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })} er du vert for: {getMealName(hostingMeal)}</p>
                  )}
                  {!hostingMeal && (
                    <p className="pending">⏳ Retten du skal servere vil bli tildelt når ruter genereres</p>
                  )}
                </div>

                {pair.notes && (
                  <div className="notes">
                    <h3>Din melding til dine gjester</h3>
                    <p>{pair.notes}</p>
                  </div>
                )}
              </div>
            )}

            {/* Routes Tab */}
            {activeTab === 'routes' && (
              <>
                {hasRoute ? (
                  <div className="details-card">
                    <h2>🗺️ Din rute</h2>
                    <div className="route-summary">
                      <div className="route-stat">
                        <span className="stat-value">{route.totalDistance.toFixed(1)} km</span>
                        <span className="stat-label">Total avstand i luftlinjer</span>
                      </div>
                      <div className="route-stat">
                        <span className="stat-value">{route.stops.filter(s => s.course !== 'party').length}</span>
                        <span className="stat-label">Retter å nyte</span>
                      </div>
                      <div className="route-stat">
                        <span className="stat-value">
                          {(() => {
                            const uniquePeople = new Set<string>();
                            route.stops.filter(s => s.course !== 'party').forEach(stop => {
                              stop.hosts.forEach(h => uniquePeople.add(h.name));
                              stop.guests.forEach(g => uniquePeople.add(g.name));
                            });
                            // Subtract 2 for the user's own pair
                            return Math.max(0, uniquePeople.size - 2);
                          })()}
                        </span>
                        <span className="stat-label">Personer å spise med</span>
                      </div>
                      <div className="route-stat">
                        <span className="stat-value">{event.totalParticipants || 0}</span>
                        <span className="stat-label">Personer på etterfesten</span>
                      </div>
                    </div>

                    <div className="route-timeline">
                      {/* Home (Starting point) */}
                      <div className="timeline-stop">
                        <div className="stop-number">0</div>
                        <div className="stop-content">
                          <div className="stop-header">
                            <h3>
                              🏠 Hjemme
                            </h3>
                            <span className="stop-time">
                              {getHomeStartTime()?.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          
                          <a 
                            href={getMapLink(pair.address)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="stop-address clickable"
                            title="Åpne i kart"
                          >
                            📍 {pair.address}
                          </a>
                          
                          <p style={{ marginTop: '8px', fontSize: '0.95rem', color: '#666' }}>
                            Start fra din hjemmeadresse. Møt opp glad, 5-10 minutter før første rett.
                          </p>
                        </div>
                      </div>

                      {route.stops.map((stop, index) => {
                        const isParty = stop.course === 'party';
                        const isHosting = !isParty && stop.hostPairId === pair.id;
                        const courseTime = isParty ? getPartyTime() : getCourseTime(stop.course);
                        const addressRevealed = isParty || isHosting || shouldRevealAddress(stop.course);
                        const guestsRevealed = shouldRevealGuests(stop.course);

                        return (
                          <>
                            {stop.distance > 0 && (
                              <div key={`distance-${index}`} style={{ fontSize: '1.15rem', color: '#6b7280', fontWeight: '500', marginBottom: '8px', marginLeft: '60px' }}>
                                🚴 {stop.distance.toFixed(1)} km i luftlinje
                              </div>
                            )}
                            <div key={index} className={`timeline-stop ${isHosting ? 'hosting' : ''} ${isParty ? 'party' : ''}`}>
                              <div className="stop-number">{index + 1}</div>
                              <div className="stop-content">
                              <div className="stop-header">
                                <h3>
                                  {getMealIcon(stop.course)} {getMealName(stop.course)}
                                </h3>
                                {courseTime && (
                                  <span className="stop-time">
                                    {courseTime.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                )}
                              </div>
                              
                              {isHosting && (
                                <div className="hosting-badge">
                                  🏠 Du er vert her
                                </div>
                              )}
                              
                              {/* Address - always shown for party */}
                              {addressRevealed ? (
                                <>
                                  <a 
                                    href={getMapLink(stop.hostAddress)} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="stop-address clickable"
                                    title="Åpne i kart"
                                  >
                                    📍 {stop.hostAddress}
                                  </a>
                                  
                                  {/* Host message - shown when address is revealed and not hosting */}
                                  {!isParty && !isHosting && stop.hostMessage && (
                                    <div className="host-message" style={{
                                      marginTop: '12px',
                                      padding: '12px',
                                      backgroundColor: '#F0FDF4',
                                      border: '1px solid #86EFAC',
                                      borderRadius: '8px',
                                      fontSize: '0.95rem',
                                      lineHeight: '1.5'
                                    }}>
                                      <strong style={{ color: '#065F46' }}>💬 Melding fra verten:</strong>
                                      <p style={{ margin: '6px 0 0 0', color: '#064E3B', whiteSpace: 'pre-wrap' }}>
                                        {stop.hostMessage}
                                      </p>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <p className="hidden-info">
                                  📍 Adressen blir synlig 30 minutter før måltidet
                                </p>
                              )}
                              
                              {/* Participant list - not shown for party */}
                              {!isParty && guestsRevealed ? (
                                <div className="stop-participants">
                                  <h4>Verter:</h4>
                                  <ul>
                                    {stop.hosts.map((host, i) => (
                                      <li key={i}>
                                        {host.name}
                                        {formatDietaryRestrictions(host.dietaryRestrictions) && ` (${formatDietaryRestrictions(host.dietaryRestrictions)})`}
                                      </li>
                                    ))}
                                  </ul>
                                  
                                  <h4>Gjester:</h4>
                                  <ul>
                                    {stop.guests.map((guest, i) => (
                                      <li key={i}>
                                        {guest.name}
                                        {formatDietaryRestrictions(guest.dietaryRestrictions) && ` (${formatDietaryRestrictions(guest.dietaryRestrictions)})`}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ) : !isParty ? (
                                <p className="hidden-info" style={{ marginTop: '12px' }}>
                                  👥 Deltakerlisten blir synlig når måltidet starter
                                </p>
                              ) : null}
                            </div>
                          </div>
                          </>
                        );
                      })}
                    </div>

                    {/* Interactive Map */}
                    {(() => {
                      // Check if we have any coordinates to show
                      const hasHomeCoords = pair.lat && pair.lon;
                      const hasStopCoords = route.stops.some(s => {
                        if (s.course === 'party') return event?.finalGatheringLocation?.lat && event?.finalGatheringLocation?.lon;
                        return s.hostLat && s.hostLon;
                      });
                      
                      if (!hasHomeCoords && !hasStopCoords) {
                        return (
                          <div style={{ 
                            marginTop: '24px', 
                            marginBottom: '24px',
                            padding: '20px',
                            backgroundColor: '#FEF3C7',
                            borderRadius: '8px',
                            textAlign: 'center'
                          }}>
                            <p>🗺️ Kartvisning ikke tilgjengelig - koordinater mangler</p>
                          </div>
                        );
                      }
                      
                      // Get first available coordinates for centering
                      const centerLat = hasHomeCoords ? pair.lat! : (route.stops[0]?.hostLat || event?.finalGatheringLocation?.lat || 60);
                      const centerLon = hasHomeCoords ? pair.lon! : (route.stops[0]?.hostLon || event?.finalGatheringLocation?.lon || 10);
                      
                      return (
                        <div style={{ marginTop: '24px', marginBottom: '24px' }}>
                          <h3 style={{ marginBottom: '12px' }}>🗺️ Kartvisning</h3>
                          <MapContainer
                            center={[centerLat, centerLon]}
                            zoom={13}
                            style={{ height: '500px', width: '100%', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
                          >
                            <TileLayer
                              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <MapBounds coordinates={
                              [
                                // Home coordinates - ALWAYS included (pin 0)
                                ...(hasHomeCoords ? [[pair.lat!, pair.lon!] as [number, number]] : []),
                                // Party location - ALWAYS included
                                ...(event?.finalGatheringLocation?.lat && event?.finalGatheringLocation?.lon 
                                  ? [[event.finalGatheringLocation.lat, event.finalGatheringLocation.lon] as [number, number]] 
                                  : []),
                                // Other stops - only if revealed
                                ...route.stops
                                  .filter((s, idx) => {
                                    if (s.course === 'party') return false; // Already added above
                                    const isHosting = s.hostPairId === pair.id;
                                    return (isHosting || shouldRevealAddress(s.course)) && s.hostLat && s.hostLon;
                                  })
                                  .map(s => [s.hostLat!, s.hostLon!] as [number, number])
                              ].filter((coord): coord is [number, number] => coord !== null && coord.length === 2 && coord[0] !== undefined && coord[1] !== undefined)
                            } />
                            
                            {/* Pin 0: Home - ALWAYS VISIBLE (your own address is always known) */}
                            {hasHomeCoords && (
                              <Marker 
                                position={[pair.lat!, pair.lon!]}
                                icon={createNumberedIcon(0, '#10B981')}
                                title="Hjemme"
                              >
                                <Popup>
                                  <div style={{ minWidth: '200px' }}>
                                    <strong style={{ fontSize: '16px' }}>🏠 Hjemme</strong><br/>
                                    <br/>
                                    <strong>📍 Vertsadresse:</strong><br/>
                                    {pair.address}<br/>
                                    <br/>
                                    <strong>⏰ Klar til start:</strong><br/>
                                    {getHomeStartTime()?.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}<br/>
                                    <em style={{ fontSize: '12px', color: '#666' }}>30 min før første rett</em>
                                  </div>
                                </Popup>
                              </Marker>
                            )}

                            {/* Route stops - progressive reveal */}
                            {route.stops.map((stop, index) => {
                              const isParty = stop.course === 'party';
                              const isHosting = !isParty && stop.hostPairId === pair.id;
                              const addressRevealed = isParty || isHosting || shouldRevealAddress(stop.course);
                              
                              // Before party starts, only show home marker (pin 0)
                              const partyTime = getPartyTime();
                              const partyStarted = partyTime && new Date() >= partyTime;
                              
                              // Don't show if address not revealed or no coordinates
                              if (!addressRevealed) return null;
                              
                              // Before party: don't show any route stops on map
                              if (!partyStarted && !isCompleted) return null;
                              
                              const lat = isParty ? event?.finalGatheringLocation?.lat : stop.hostLat;
                              const lon = isParty ? event?.finalGatheringLocation?.lon : stop.hostLon;
                              
                              if (!lat || !lon) return null;

                              const stopTime = isParty ? partyTime : getCourseTime(stop.course);
                              const stopNumber = index + 1;
                              const stopColor = isHosting ? '#F59E0B' : isParty ? '#DC2626' : '#4287f5';
                              const mealName = getMealName(stop.course);

                              return (
                                <Marker
                                  key={index}
                                  position={[lat, lon]}
                                  icon={createNumberedIcon(stopNumber, stopColor)}
                                  title={`${mealName} - ${stopTime?.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}`}
                                >
                                  <Popup>
                                    <div style={{ minWidth: '200px' }}>
                                      <strong style={{ fontSize: '16px' }}>
                                        {getMealIcon(stop.course)} {getMealName(stop.course)}
                                      </strong><br/>
                                      {isHosting && <span style={{ color: '#F59E0B', fontWeight: 'bold' }}>⭐ Du er vert her</span>}<br/>
                                      <br/>
                                      <strong>⏰ Tid:</strong><br/>
                                      {stopTime?.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}<br/>
                                      <br/>
                                      <strong>📍 Adresse:</strong><br/>
                                      {isParty ? event?.finalGatheringLocation?.name : stop.hostAddress}<br/>
                                      <br/>
                                      {!isParty && stop.hostNames && (
                                        <>
                                          <strong>👥 Verter:</strong><br/>
                                          {stop.hostNames}<br/>
                                          <br/>
                                        </>
                                      )}
                                      {stop.distance > 0 && (
                                        <>
                                          <strong>🚴 Fra forrige:</strong><br/>
                                          {stop.distance.toFixed(1)} km<br/>
                                        </>
                                      )}
                                    </div>
                                  </Popup>
                                </Marker>
                              );
                            })}

                            {/* Polylines connecting consecutive revealed stops with distance labels */}
                            {(() => {
                              // Build array of all positions with their reveal status
                              const allPositions = [
                                { 
                                  lat: hasHomeCoords ? pair.lat! : null, 
                                  lon: hasHomeCoords ? pair.lon! : null,
                                  revealed: true, // Home is always revealed
                                  index: -1,
                                  distance: 0
                                },
                                ...route.stops.map((s, idx) => {
                                  const isParty = s.course === 'party';
                                  const isHosting = !isParty && s.hostPairId === pair.id;
                                  const addressRevealed = isParty 
                                    ? (event?.finalGatheringLocation?.lat && event?.finalGatheringLocation?.lon) // Party only revealed if location exists
                                    : (isHosting || shouldRevealAddress(s.course));
                                  
                                  const lat = isParty ? event?.finalGatheringLocation?.lat : s.hostLat;
                                  const lon = isParty ? event?.finalGatheringLocation?.lon : s.hostLon;
                                  
                                  return {
                                    lat: lat || null,
                                    lon: lon || null,
                                    revealed: addressRevealed,
                                    index: idx,
                                    distance: s.distance
                                  };
                                })
                              ];

                              return (
                                <>
                                  {/* Draw individual line segments only between consecutive revealed stops */}
                                  {allPositions.map((pos, i) => {
                                    if (i === 0) return null; // Skip first position (it has no previous)
                                    
                                    const prevPos = allPositions[i - 1];
                                    
                                    // Only draw line if both current and previous are revealed and have coordinates
                                    if (!pos.revealed || !prevPos.revealed) return null;
                                    if (!pos.lat || !pos.lon || !prevPos.lat || !prevPos.lon) return null;
                                    
                                    return (
                                      <Polyline
                                        key={`line-${i}`}
                                        positions={[[prevPos.lat, prevPos.lon], [pos.lat, pos.lon]]}
                                        pathOptions={{ color: '#4287f5', weight: 3, opacity: 0.7 }}
                                      />
                                    );
                                  })}
                                  
                                  {/* Distance labels on route segments - only show for revealed segments */}
                                  {allPositions.map((pos, i) => {
                                    if (i === 0) return null; // Skip home position
                                    if (pos.distance === 0) return null;
                                    
                                    const prevPos = allPositions[i - 1];
                                    
                                    // Only show distance label if both current and previous are revealed
                                    if (!pos.revealed || !prevPos.revealed) return null;
                                    if (!pos.lat || !pos.lon || !prevPos.lat || !prevPos.lon) return null;
                                    
                                    // Calculate midpoint for label
                                    const midLat = (prevPos.lat + pos.lat) / 2;
                                    const midLon = (prevPos.lon + pos.lon) / 2;
                                    
                                    return (
                                      <Marker
                                        key={`distance-${i}`}
                                        position={[midLat, midLon]}
                                        icon={L.divIcon({
                                          className: 'distance-label',
                                          html: `<div style="
                                            background: white;
                                            padding: 4px 8px;
                                            border-radius: 12px;
                                            border: 2px solid #4287f5;
                                            font-size: 12px;
                                            font-weight: bold;
                                            color: #4287f5;
                                            white-space: nowrap;
                                            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                                          ">🚴 ${pos.distance.toFixed(1)} km</div>`,
                                          iconSize: [80, 24],
                                          iconAnchor: [40, 12]
                                        })}
                                      />
                                    );
                                  })}
                                </>
                              );
                            })()}
                          </MapContainer>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="details-card info-card">
                    <h2>⏳ {event.status === 'registration' ? 'Venter på ruter' : 'Ruter genereres'}</h2>
                    <p>
                      {event.status === 'registration' 
                        ? 'Rutene vil bli generert etter påmeldingsfristen. Du vil få beskjed når ruten din er klar!'
                        : 'Arrangøren holder på å generere optimale ruter for alle deltakere. Dette kan ta litt tid.'}
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Timeline Tab */}
            {activeTab === 'timeline' && (
              <>
                {timeline ? (
                  <EventTimeline eventName={event.name} eventDate={event.scheduledDate} timeline={timeline} />
                ) : (
                  <div className="details-card">
                    <p>Ingen tidsplan tilgjengelig ennå.</p>
                  </div>
                )}
              </>
            )}

            {/* Logs Tab */}
            {activeTab === 'logs' && (
              <div className="details-card">
                <h2>E-postvarslinger logg</h2>
                <p style={{ marginBottom: '20px', color: '#666' }}>
                  Oversikt over e-postvarslinger som er levert for dette arrangementet.
                </p>

                {logsLoading ? (
                  <p>Laster logger...</p>
                ) : logs.length === 0 ? (
                  <p>Ingen e-postvarslinger sendt ennå.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {logs.map((log) => {
                      const typeLabels: Record<string, string> = {
                        host_notification: '🍽️ Vertsvarsling',
                        registration_confirmation: '✅ Påmeldingsbekreftelse',
                        organizer_message: '💬 Arrangørmelding',
                        event_cancellation: '❌ Avlysning',
                        event_day_firstStop: '🚴 Først stopp-varsling',
                        event_day_mainCourse: '🚴 Hovedrett-varsling',
                        event_day_dessert: '🚴 Dessert-varsling',
                        event_day_finalDestination: '🚴 Siste stopp-varsling'
                      };
                      const typeLabel = typeLabels[log.type] || log.type;
                      const isUnverified = log.status === 'skipped' && log.errorMessage === 'Email not verified';

                      return (
                        <div
                          key={log.id}
                          style={{
                            padding: '16px',
                            borderRadius: '8px',
                            border: `2px solid ${log.status === 'success' ? '#10B981' : isUnverified ? '#F59E0B' : '#EF4444'}`,
                            backgroundColor: log.status === 'success' ? '#F0FDF4' : isUnverified ? '#FEF3C7' : '#FEF2F2'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                            <div>
                              <strong style={{ color: log.status === 'success' ? '#065F46' : isUnverified ? '#78350F' : '#991B1B' }}>
                                {log.status === 'success' ? '✅ Sendt' : isUnverified ? '⚠️ Ikke Sendt - Epostadresse ikke verifisert' : '❌ Feilet'}
                              </strong>
                              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>
                                {new Date(log.timestamp).toLocaleString('nb-NO', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                              {isUnverified && (
                                <a 
                                  href="/deltaker/profil" 
                                  style={{ 
                                    display: 'inline-block',
                                    marginTop: '8px', 
                                    padding: '4px 12px', 
                                    backgroundColor: '#2563EB', 
                                    color: 'white', 
                                    borderRadius: '4px', 
                                    textDecoration: 'none',
                                    fontSize: '13px',
                                    fontWeight: '600'
                                  }}
                                >
                                  Verifiser e-post
                                </a>
                              )}
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                backgroundColor: '#E0E7FF',
                                fontSize: '13px',
                                fontWeight: '600'
                              }}>
                                {typeLabel}
                              </span>
                              {log.course && (
                                <span style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  backgroundColor: log.course === 'appetizer' ? '#DBEAFE' : log.course === 'mainCourse' ? '#FEF3C7' : '#FCE7F3',
                                  fontSize: '13px',
                                  marginLeft: '8px'
                                }}>
                                  {log.course === 'appetizer' ? '🥗 Forrett' : log.course === 'mainCourse' ? '🍽️ Hovedrett' : '🍰 Dessert'}
                                </span>
                              )}
                            </div>
                          </div>

                          <div style={{ marginBottom: '8px' }}>
                            <strong>Emne:</strong> {log.subject}
                          </div>

                          {log.emailBodyPlainText && (
                            <div style={{ marginTop: '16px', borderTop: '1px solid #E5E7EB', paddingTop: '12px' }}>
                              <button
                                onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: 'var(--primary-color)',
                                  cursor: 'pointer',
                                  padding: '0',
                                  fontSize: '14px',
                                  fontWeight: '600',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                              >
                                {expandedLogId === log.id ? '▼' : '▶'} Se e-postens innhold
                              </button>
                              {expandedLogId === log.id && (
                                <div style={{
                                  marginTop: '12px',
                                  padding: '16px',
                                  backgroundColor: '#F9FAFB',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  fontFamily: 'monospace',
                                  whiteSpace: 'pre-wrap',
                                  color: '#374151',
                                  lineHeight: '1.6',
                                  maxHeight: '400px',
                                  overflowY: 'auto'
                                }}>
                                  {log.emailBodyPlainText}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="details-card">
                <h2>Innstillinger</h2>
                
                <div className="danger-zone" style={{ marginTop: '32px' }}>
                  <h3 style={{ color: '#EF4444', marginBottom: '12px' }}>⚠️ Farlig sone</h3>
                  {canUnsubscribe() ? (
                    <>
                      <p style={{ marginBottom: '20px' }}>
                        Når du melder deg av arrangementet, kan det ikke angres. Du og din partner vil motta en e-post som bekrefter avmeldingen.
                      </p>
                      <button 
                        className="btn btn-danger" 
                        onClick={() => setShowUnsubscribeConfirm(true)}
                        disabled={unsubscribing}
                      >
                        {unsubscribing ? 'Melder av...' : '❌ Meld deg av arrangementet'}
                      </button>
                      <p style={{ fontSize: '14px', color: '#666', marginTop: '12px' }}>
                        Du kan melde deg av så lenge påmeldingen er åpen og det er mer enn 24 timer til fristen.
                      </p>
                    </>
                  ) : (
                    <p style={{ color: '#666' }}>
                      Avmelding er ikke mulig. Fristen for avmelding er passert eller påmeldingen er stengt.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Unsubscribe Confirmation Modal */}
      {showUnsubscribeConfirm && (
        <div className="modal-overlay" onClick={() => !unsubscribing && setShowUnsubscribeConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Bekreft avmelding</h2>
            <p>Er du sikker på at du vil melde deg og din partner av <strong>{event.name}</strong>?</p>
            <p className="warning-text">
              ⚠️ Dette kan ikke angres. Dere vil motta en e-post som bekrefter avmeldingen.
            </p>
            <div className="modal-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowUnsubscribeConfirm(false)}
                disabled={unsubscribing}
              >
                Avbryt
              </button>
              <button 
                className="btn btn-danger" 
                onClick={handleUnsubscribe}
                disabled={unsubscribing}
              >
                {unsubscribing ? 'Melder av...' : 'Ja, meld av'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetails;
